<template>
  <form class="conversation--form w-full" @submit.prevent="onFormSubmit">
    <div v-if="showNoInboxAlert" class="callout warning">
      <p>
        {{ $t('NEW_CONVERSATION.NO_INBOX') }}
      </p>
    </div>
    <div v-else>
      <div class="gap-2 flex flex-row">
        <div class="w-[50%]">
          <label>
            {{ $t('NEW_CONVERSATION.FORM.INBOX.LABEL') }}
            <span class="text-red-500">*</span>
          </label>
          <div class="multiselect-wrap--small">
            <multiselect
              v-model="targetInbox"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              selected-label=""
              select-label=""
              deselect-label=""
              :max-height="160"
              :close-on-select="true"
              :options="[...inboxes]"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                <inbox-dropdown-item
                  v-if="option.name"
                  :name="option.name"
                  :inbox-identifier="computedInboxSource(option)"
                  :channel-type="option.channel_type"
                />
                <span v-else>
                  {{ $t('NEW_CONVERSATION.FORM.INBOX.PLACEHOLDER') }}
                </span>
              </template>
              <template slot="option" slot-scope="{ option }">
                <inbox-dropdown-item
                  :name="option.name"
                  :inbox-identifier="computedInboxSource(option)"
                  :channel-type="option.channel_type"
                />
              </template>
            </multiselect>
          </div>
          <label :class="{ error: $v.targetInbox.$error }">
            <span v-if="$v.targetInbox.$error" class="message">
              {{ $t('NEW_CONVERSATION.FORM.INBOX.ERROR') }}
            </span>
          </label>
        </div>
        <div class="w-[50%]">
          <label>
            {{ $t('NEW_CONVERSATION.FORM.TO.LABEL') }}
            <div
              class="flex items-center h-[2.4735rem] rounded-sm py-1 px-2 bg-slate-25 dark:bg-slate-900 border border-solid border-slate-75 dark:border-slate-600"
            >
              <thumbnail
                :src="contact.thumbnail"
                size="24px"
                :username="contact.name"
                :status="contact.availability_status"
              />
              <h4
                class="m-0 ml-2 mr-2 text-slate-700 dark:text-slate-100 text-sm"
                :title="contact.name.length > 15 ? contact.name : ''"
              >
                {{ contact.name.length > 15 ? contact.name.substring(0,15) + '...' :contact.name }}
              </h4>
            </div>
          </label>
        </div>
      </div>
      <div v-if="isAnEmailInbox" class="w-full">
        <div class="w-full">
          <div class="flex items-center justify-between mb-1">
            <label :for="'subject-input'" :class="{ error: $v.subject.$error }">
              <span>
                {{ $t('NEW_CONVERSATION.FORM.SUBJECT.LABEL') }}<span class="text-red-500">*</span>
              </span>
            </label>
            <email-suggestion-button
              v-if="contact.id"
              :contact-id="contact.id"
              @suggestion-generated="handleSuggestionGenerated"
            />
          </div>
          <label :class="{ error: $v.subject.$error }">
            <input
              id="subject-input"
              v-model="subject"
              type="text"
              :placeholder="$t('NEW_CONVERSATION.FORM.SUBJECT.PLACEHOLDER')"
              @input="$v.subject.$touch"
            />
            <span v-if="$v.subject.$error" class="message">
              {{ $t('NEW_CONVERSATION.FORM.SUBJECT.ERROR') }}
            </span>
          </label>
        </div>
      </div>
      <div class="w-full">
        <div class="w-full">
          <div class="relative">
            <canned-response
              v-if="showCannedResponseMenu && hasSlashCommand"
              :search-key="cannedResponseSearchKey"
              @click="replaceTextWithCannedResponse"
            />
          </div>
          <div v-if="isEmailOrWebWidgetInbox" class="message-section">
            <!-- Chip inputs must stay outside <label> or clicks hit the chip remove button -->
            <label class="message-section__label">
              {{ $t('NEW_CONVERSATION.FORM.MESSAGE.LABEL') }}
            </label>
            <reply-email-head
              v-if="isAnEmailInbox"
              ref="replyEmailHead"
              :cc-emails.sync="ccEmails"
              :bcc-emails.sync="bccEmails"
              :allow-to-field="false"
            />
            <span
              v-if="isAnEmailInbox && $v.ccEmails.$error"
              class="message"
            >
              {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.ERROR') }}
            </span>
            <span
              v-if="isAnEmailInbox && $v.bccEmails.$error"
              class="message"
            >
              {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.ERROR') }}
            </span>
            <div class="editor-wrap">
              <woot-message-editor
                v-model="message"
                class="message-editor"
                :class="{ editor_warning: $v.message.$error }"
                :enable-variables="true"
                :placeholder="$t('NEW_CONVERSATION.FORM.MESSAGE.PLACEHOLDER')"
                @toggle-canned-menu="toggleCannedMenu"
                @blur="$v.message.$touch"
              />
              <span v-if="$v.message.$error" class="editor-warning__message">
                {{ $t('NEW_CONVERSATION.FORM.MESSAGE.ERROR') }}
              </span>
            </div>
            <!-- Attachment Preview -->
            <div
              v-if="composerAttachmentItems.length"
              class="attachment-preview-box"
            >
              <attachment-preview
                :attachments="composerAttachmentItems"
                :remove-attachment="removeAttachment"
                @cancel-upload="cancelUpload"
              />
            </div>
            <!-- File Upload Button for Email Inbox - Using native input to avoid conflicts -->
            <div v-if="isAnEmailInbox" class="attachment-button-wrap mt-2">
              <input
                ref="modalFileInput"
                type="file"
                :accept="allowedFileTypes"
                multiple
                class="hidden-file-input"
                @change="onNativeFileUpload"
              />
              <woot-button
                type="button"
                class-names="button--upload"
                :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
                icon="clipboard"
                emoji="📎"
                color-scheme="secondary"
                variant="smooth"
                size="small"
                @click.stop.prevent="openFileSelector"
              >
                {{ $t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON') }}
              </woot-button>
              <woot-button
                v-if="isSignatureAvailable"
                type="button"
                v-tooltip="{
                  content: $t('CONVERSATION.FOOTER.MESSAGE_SIGN_TOOLTIP'),
                  placement: 'top',
                  classes: 'tooltip-above-modal',
                }"
                icon="signature"
                color-scheme="secondary"
                :variant="sendWithSignature ? 'clear' : 'smooth'"
                :class-names="signatureButtonClassNames"
                size="small"
                @click.stop.prevent="toggleMessageSignature"
              />
            </div>
            <!-- Signature preview (only when email inbox + signature configured + toggle is on) -->
            <div
              v-if="isSignatureEnabledForInbox"
              v-tooltip="$t('CONVERSATION.FOOTER.MESSAGE_SIGN_TOOLTIP')"
              class="message-signature-wrap"
            >
              <p
                v-dompurify-html="formatMessage(messageSignature)"
                class="message-signature"
              />
            </div>
          </div>
          <whatsapp-templates
            v-else-if="hasWhatsappTemplates"
            :inbox-id="selectedInbox.inbox.id"
            @on-select-template="toggleWaTemplate"
            @on-send="onSendWhatsAppReply"
          />
          <label v-else :class="{ error: $v.message.$error }">
            {{ $t('NEW_CONVERSATION.FORM.MESSAGE.LABEL') }}
            <span class="text-red-500">*</span>
            <textarea
              v-model="message"
              class="min-h-[5rem]"
              type="text"
              :placeholder="$t('NEW_CONVERSATION.FORM.MESSAGE.PLACEHOLDER')"
              @input="$v.message.$touch"
            />
            <span v-if="$v.message.$error" class="message">
              {{ $t('NEW_CONVERSATION.FORM.MESSAGE.ERROR') }}
            </span>
          </label>
        </div>
      </div>
    </div>
    <div
      v-if="!hasWhatsappTemplates"
      class="flex flex-row justify-end gap-2 py-2 px-0 w-full"
    >
      <button class="button clear" @click.prevent="onCancel">
        {{ $t('NEW_CONVERSATION.FORM.CANCEL') }}
      </button>
      <woot-button
        type="submit"
        class="button-color"
        :is-disabled="hasActiveUploads"
        :is-loading="conversationsUiFlags.isCreating"
      >
        {{ $t('NEW_CONVERSATION.FORM.SUBMIT') }}
      </woot-button>
    </div>
  </form>
</template>

<script>
import { mapGetters } from 'vuex';
import Thumbnail from 'dashboard/components/widgets/Thumbnail';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';
import ReplyEmailHead from 'dashboard/components/widgets/conversation/ReplyEmailHead';
import CannedResponse from 'dashboard/components/widgets/conversation/CannedResponse.vue';
import InboxDropdownItem from 'dashboard/components/widgets/InboxDropdownItem';
import EmailSuggestionButton from 'dashboard/components/widgets/EmailSuggestionButton.vue';
import WhatsappTemplates from './WhatsappTemplates.vue';
import alertMixin from 'shared/mixins/alertMixin';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { getInboxSource } from 'dashboard/helper/inbox';
import { required, requiredIf } from 'vuelidate/lib/validators';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview';
import {
  ALLOWED_FILE_TYPES,
  MAXIMUM_FILE_UPLOAD_SIZE,
  MAXIMUM_FILE_UPLOAD_SIZE_EMAIL_CHANNEL,
} from 'shared/constants/messages';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { validEmailsByComma } from 'dashboard/components/widgets/conversation/helpers/emailHeadHelper';

export default {
  components: {
    Thumbnail,
    WootMessageEditor,
    ReplyEmailHead,
    CannedResponse,
    WhatsappTemplates,
    InboxDropdownItem,
    EmailSuggestionButton,
    AttachmentPreview,
  },
  mixins: [alertMixin, messageFormatterMixin, uiSettingsMixin],
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    onSubmit: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      name: '',
      subject: '',
      message: '',
      showCannedResponseMenu: false,
      cannedResponseSearchKey: '',
      bccEmails: '',
      ccEmails: '',
      targetInbox: {},
      whatsappTemplateSelected: false,
      attachedFiles: [],
      activeUploads: [],
      attachmentOrder: 0,
      submitUploadProgress: null,
    };
  },
  validations: {
    subject: {
      required: requiredIf('isAnEmailInbox'),
    },
    message: {
      required,
    },
    targetInbox: {
      required,
    },
    ccEmails: {
      hasValidEmails(value) {
        if (!this.isAnEmailInbox) return true;

        return validEmailsByComma(value);
      },
    },
    bccEmails: {
      hasValidEmails(value) {
        if (!this.isAnEmailInbox) return true;

        return validEmailsByComma(value);
      },
    },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      conversationsUiFlags: 'getContactSidebarUiFlags',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      accountId: 'getCurrentAccountId',
      messageSignature: 'getEmailMessageSignature',
    }),
    // Mirrors ReplyBox / ReplyBottomPanel so the user's signature preference is
    // consistent across the app. The toggle persists via UI settings.
    sendWithSignature() {
      const { send_with_signature: isEnabled } = this.uiSettings;
      return !!isEnabled;
    },
    isSignatureAvailable() {
      return !!this.messageSignature;
    },
    // Restrict signature handling to Email channel only and require a
    // configured signature — without one, the toggle is hidden and nothing
    // is appended to the outgoing message.
    isSignatureEnabledForInbox() {
      return (
        this.isAnEmailInbox &&
        this.isSignatureAvailable &&
        this.sendWithSignature
      );
    },
    // Blue highlight class only when signature is on.
    signatureButtonClassNames() {
      return this.sendWithSignature ? 'message-signature-toggle--on' : '';
    },
    hasActiveUploads() {
      return this.activeUploads.length > 0;
    },
    composerAttachmentItems() {
      const uploads = this.activeUploads.map(upload => ({
        id: upload.clientId || upload.id,
        clientId: upload.clientId,
        uploading: true,
        uploadId: upload.id,
        order: upload.order,
        progress: upload.progress,
        thumb: upload.thumb,
        resource: {
          name: upload.name,
          filename: upload.name,
          type: upload.type,
          content_type: upload.type,
          size: upload.size,
        },
      }));
      // While the create request is sending files, every attached chip shows
      // that request's upload progress.
      const isSending = this.submitUploadProgress !== null;
      const ready = this.attachedFiles.map(file => ({
        ...file,
        id: file.clientId,
        uploading: isSending,
        progress: isSending ? this.submitUploadProgress : 0,
        order: file.order || 0,
      }));
      return [...uploads, ...ready].sort(
        (left, right) => (left.order || 0) - (right.order || 0)
      );
    },
    allowedFileTypes() {
      return ALLOWED_FILE_TYPES;
    },
    // Email channels use the tighter 20 MB cap; other channels use 40 MB.
    maxFileUploadSize() {
      return this.isAnEmailInbox
        ? MAXIMUM_FILE_UPLOAD_SIZE_EMAIL_CHANNEL
        : MAXIMUM_FILE_UPLOAD_SIZE;
    },
    emailMessagePayload() {
      const payload = {
        inboxId: this.targetInbox.id,
        sourceId: this.targetInbox.sourceId,
        contactId: this.contact.id,
        message: { content: this.messageWithSignature },
        mailSubject: this.subject,
        assigneeId: this.currentUser.id,
      };
      if (this.ccEmails) {
        payload.message.cc_emails = this.ccEmails;
      }

      if (this.bccEmails) {
        payload.message.bcc_emails = this.bccEmails;
      }

      // Add files for FormData upload
      if (this.attachedFiles && this.attachedFiles.length) {
        payload.files = this.attachedFiles.map(attachment => attachment.file);
      }

      return payload;
    },
    // Append the user's message signature only for email inbox when the toggle
    // is enabled and a signature is configured. Matches ReplyBox.vue's
    // convention of joining the body and signature with two newlines.
    messageWithSignature() {
      if (this.isSignatureEnabledForInbox && this.messageSignature) {
        return `${this.message}\n\n${this.messageSignature}`;
      }
      return this.message;
    },
    selectedInbox: {
      get() {
        const inboxList = this.contact.contactableInboxes || [];
        return (
          inboxList.find(inbox => {
            return inbox.inbox?.id && inbox.inbox?.id === this.targetInbox?.id;
          }) || {
            inbox: {},
          }
        );
      },
      set(value) {
        this.targetInbox = value.inbox;
      },
    },
    showNoInboxAlert() {
      if (!this.contact.contactableInboxes) {
        return false;
      }
      return this.inboxes.length === 0 && !this.uiFlags.isFetchingInboxes;
    },
    inboxes() {
      const inboxList = this.contact.contactableInboxes || [];
      return inboxList.map(inbox => ({
        ...inbox.inbox,
        sourceId: inbox.source_id,
      }));
    },
    isAnEmailInbox() {
      return (
        this.selectedInbox &&
        this.selectedInbox.inbox.channel_type === INBOX_TYPES.EMAIL
      );
    },
    isAnWebWidgetInbox() {
      return (
        this.selectedInbox &&
        this.selectedInbox.inbox.channel_type === INBOX_TYPES.WEB
      );
    },
    isEmailOrWebWidgetInbox() {
      return this.isAnEmailInbox || this.isAnWebWidgetInbox;
    },
    hasWhatsappTemplates() {
      return !!this.selectedInbox.inbox?.message_templates;
    },
  },
  watch: {
    message(value) {
      this.hasSlashCommand = value[0] === '/' && !this.isEmailOrWebWidgetInbox;
      const hasNextWord = value.includes(' ');
      const isShortCodeActive = this.hasSlashCommand && !hasNextWord;
      if (isShortCodeActive) {
        this.cannedResponseSearchKey = value.substring(1);
        this.showCannedResponseMenu = true;
      } else {
        this.cannedResponseSearchKey = '';
        this.showCannedResponseMenu = false;
      }
    },
  },
  beforeDestroy() {
    this.abortActiveUploads();
  },
  methods: {
    onCancel() {
      this.abortActiveUploads();
      this.$emit('cancel');
    },
    onSuccess() {
      this.$emit('success');
    },
    replaceTextWithCannedResponse(message) {
      this.message = message;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    // Persist the signature preference globally via UI settings so it stays in
    // sync with the same toggle in ReplyBox / ReplyBottomPanel.
    toggleMessageSignature() {
      this.updateUISettings({
        send_with_signature: !this.sendWithSignature,
      });
    },
    prepareWhatsAppMessagePayload({ message: content, templateParams }) {
      const payload = {
        inboxId: this.targetInbox.id,
        sourceId: this.targetInbox.sourceId,
        contactId: this.contact.id,
        message: { content, template_params: templateParams },
        assigneeId: this.currentUser.id,
      };
      return payload;
    },
    onFormSubmit() {
      if (this.hasActiveUploads) {
        return;
      }
      if (this.isAnEmailInbox && this.$refs.replyEmailHead) {
        if (!this.$refs.replyEmailHead.flushPendingEmails()) {
          return;
        }
      }
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.createConversation(this.emailMessagePayload);
    },
    async createConversation(payload) {
      const hasFiles = payload.files && payload.files.length > 0;
      if (hasFiles) {
        this.submitUploadProgress = 0;
        payload.onUploadProgress = event => {
          if (!event.total) return;
          this.submitUploadProgress = Math.min(
            100,
            Math.round((event.loaded / event.total) * 100)
          );
        };
      }
      try {
        const data = await this.onSubmit(payload);
        const action = {
          type: 'link',
          to: `/app/accounts/${data.account_id}/conversations/${data.id}`,
          message: this.$t('NEW_CONVERSATION.FORM.GO_TO_CONVERSATION'),
        };
        this.onSuccess();
        this.showAlert(
          this.$t('NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'),
          action
        );
        // Clear attachments after successful submission
        this.attachedFiles = [];
      } catch (error) {
        if (error instanceof ExceptionWithMessage) {
          this.showAlert(error.data);
        } else {
          this.showAlert(this.$t('NEW_CONVERSATION.FORM.ERROR_MESSAGE'));
        }
      } finally {
        this.submitUploadProgress = null;
      }
    },

    toggleWaTemplate(val) {
      this.whatsappTemplateSelected = val;
    },
    async onSendWhatsAppReply(messagePayload) {
      const payload = this.prepareWhatsAppMessagePayload(messagePayload);
      await this.createConversation(payload);
    },
    inboxReadableIdentifier(inbox) {
      return `${inbox.name} (${inbox.channel_type})`;
    },
    computedInboxSource(inbox) {
      if (!inbox.channel_type) return '';
      const classByType = getInboxSource(
        inbox.channel_type,
        inbox.phone_number,
        inbox
      );
      return classByType;
    },
    handleSuggestionGenerated({ subject, message }) {
      // Auto-fill the subject and message fields with AI-generated content
      this.subject = subject;
      this.message = message;
      
      // Touch validations to remove any error states
      this.$v.subject.$touch();
      this.$v.message.$touch();
    },
    // File upload methods - using native input to avoid conflicts with ReplyBottomPanel
    openFileSelector() {
      this.$refs.modalFileInput.click();
    },
    onNativeFileUpload(event) {
      const files = event.target.files;
      if (!files || files.length === 0) {
        return;
      }

      const maxSize = this.maxFileUploadSize;

      Array.from(files).forEach(file => {
        if (checkFileSizeLimit(file, maxSize)) {
          this.uploadAttachment(file);
        } else {
          this.showAlert(
            this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
              MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: maxSize,
            })
          );
        }
      });

      event.target.value = '';
    },
    // Files are still sent with the create-conversation request; the loader
    // covers reading each file in the browser so Submit waits until all are ready.
    uploadAttachment(file) {
      this.attachmentOrder += 1;
      const entry = {
        id: `upl-${Date.now()}-${this.attachmentOrder}`,
        clientId: `att-${Date.now()}-${this.attachmentOrder}`,
        order: this.attachmentOrder,
        name: file.name || 'file',
        type: file.type || '',
        size: file.size || 0,
        progress: 0,
        thumb: '',
        cancelled: false,
        reader: null,
      };
      this.activeUploads.push(entry);

      const reader = new FileReader();
      entry.reader = reader;
      reader.onprogress = event => {
        if (!event.lengthComputable || entry.cancelled) return;
        entry.progress = Math.min(
          100,
          Math.round((event.loaded / event.total) * 100)
        );
      };
      reader.onload = () => {
        if (entry.cancelled) return;
        this.attachedFiles.push({
          clientId: entry.clientId,
          order: entry.order,
          resource: file,
          file,
          thumb: typeof reader.result === 'string' ? reader.result : '',
        });
      };
      reader.onerror = () => {
        if (entry.cancelled) return;
        this.showAlert(this.$t('CONVERSATION.MESSAGE_ERROR'));
      };
      reader.onloadend = () => {
        this.finishUpload(entry);
      };
      reader.readAsDataURL(file);
    },
    finishUpload(entry) {
      if (!entry) return;
      this.activeUploads = this.activeUploads.filter(
        item => item.id !== entry.id
      );
    },
    abortActiveUploads() {
      this.activeUploads.slice().forEach(entry => this.cancelUpload(entry.id));
    },
    cancelUpload(uploadId) {
      const entry = this.activeUploads.find(item => item.id === uploadId);
      if (!entry) return;
      entry.cancelled = true;
      if (entry.reader && entry.reader.readyState === FileReader.LOADING) {
        entry.reader.abort();
      }
      this.finishUpload(entry);
    },
    removeAttachment(itemIndex) {
      if (typeof itemIndex === 'string') {
        this.attachedFiles = this.attachedFiles.filter(
          item => item.clientId !== itemIndex
        );
        return;
      }
      this.attachedFiles.splice(itemIndex, 1);
    },
  },
};
</script>

<style lang="scss">
.conversation--form {
  @apply pt-4 px-8 pb-8;
}

.message-section {
  @apply w-full;

  &__label {
    @apply block mb-1;
  }
}

.message-editor {
  @apply px-3;

  :deep {
    .ProseMirror-menubar {
      @apply rounded-tl-[4px];
    }
  }
}

:deep {
  .mention--box {
    @apply left-0 m-auto right-0 top-auto h-fit;
  }

  /* TODO: Remove when have standardized a component out of multiselect  */
  .multiselect .multiselect__content .multiselect__option span {
    @apply inline-flex w-6 text-slate-600 dark:text-slate-400;
  }
  .multiselect .multiselect__content .multiselect__option {
    @apply py-0.5 px-1;
  }
  .button-color {
    background-color: #40518a;
  }
}

.attachment-button-wrap {
  @apply flex items-center gap-2;
}

.attachment-preview-box {
  @apply mt-2 mb-2;
}

.hidden-file-input {
  @apply hidden;
}

.message-signature-wrap {
  @apply mt-2 mx-0 px-2 py-1 flex max-h-[8vh] items-baseline justify-between hover:bg-slate-25 dark:hover:bg-slate-800 border border-dashed border-slate-100 dark:border-slate-700 rounded-sm overflow-auto;
}

// Enabled-signature highlight; matches EmailSuggestionButton blue.
// `!important` overrides .button.clear.secondary hover/focus from
// scss/widgets/_buttons.scss which has equal specificity.
.button.message-signature-toggle--on,
.button.message-signature-toggle--on:hover,
.button.message-signature-toggle--on:focus,
.button.message-signature-toggle--on:active {
  background-color: #1f93ff !important;
  color: #fff !important;

  .icon,
  svg {
    color: #fff !important;
  }
}

.button.message-signature-toggle--on:hover:not(:disabled) {
  background-color: #007ef3 !important;
}

.message-signature {
  @apply w-fit m-0;

  :deep p:last-child {
    @apply mb-0;
  }
}
</style>
